import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Divider, Form, Input, Modal, notification } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  createCategory,
  getCategoryById,
  updateCategoryById,
} from "store/slices/categoriesSlice";
import { closeModal } from "store/slices/modalsSlice";
import CategoryForm from "./category-form";

const EditCategoryModal = () => {
  const { t } = useTranslation();
  const [form] = Form?.useForm();
  const dispatch = useDispatch();
  const { isModalOpen, modalType, modalProps } = useSelector(
    (state) => state?.modals
  );
  const { category, loading } = useSelector((state) => state?.categories);

  const open = isModalOpen && modalType === "edit_category";

  useEffect(() => {
    if (modalProps?.categoryId && open) {
      dispatch(getCategoryById(modalProps?.categoryId));
    }
  }, [dispatch, modalProps, open]);

  useEffect(() => {
    if (category && open) {
      console?.log("Category to edit:", category);
      // Format subCategories for the form - keep their original IDs
      const formattedCategory = {
        ...category,
        subCategories:
          category?.subCategories?.map((subCat) => ({
            _id: subCat?._id, // Keep the original _id for identification
            titled: subCat?.titled, 
            description: subCat?.description,
          })) || [],
      };
      form?.setFieldsValue(formattedCategory);
    } else if (!category) {
      form?.resetFields();
    }
  }, [form, category, open]);

  const onSubmit = async (values) => {
    // Transform the values for the API
    const transformedValues = {
      titled: values?.titled,
      description: values?.description,
      subCategories: [],
    };

    // Process subCategories if they exist
    if (values?.subCategories && values?.subCategories?.length > 0) {
      transformedValues.subCategories = values?.subCategories?.map((subCat) => {
        // Format for update API
        if (subCat?._id) {
          // Existing subCategory with ID
          return {
            subCategoryId: subCat?._id, // Use original ID for update
            titled: subCat?.titled, // API expects 'title', not 'titled'
            description: subCat?.description,
          };
        } else {
          // New subCategory without ID
          return {
            titled: subCat?.titled, // API expects 'title', not 'titled'
            description: subCat?.description,
          };
        }
      });
    }

    console?.log("Submitting updated category data:", transformedValues);

    try {
      await dispatch(
        updateCategoryById({
          id: modalProps?.categoryId,
          data: transformedValues,
        })
      )?.unwrap();

      notification?.success({
        message: t("categories.crud.form.success"),
      });
      onClose();
    } catch (error) {
      console?.error("Error updating category:", error);
      notification?.error({
        message: t("categories.crud.form.error.failed"),
        description: error?.message || "Une erreur s'est produite",
        role: "alert",
        duration: 3,
      });
    }
  };

  const onClose = () => {
    form?.resetFields();
    dispatch(closeModal());
  };

  return (
    <Modal
      title={
        <h4 style={{ textAlign: "center" }}>{t("categories.crud.form")}</h4>
      }
      open={open}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t("categories.crud.form.button.cancel")}
        </Button>,
        <Button
          key="submit"
          type="primary"
          htmlType="submit"
          form="category_form"
          disabled={loading}
        >
          {t("categories.crud.form.button.save")}
        </Button>,
      ]}
      centered
      styles={{ content: { overflowY: "auto", maxHeight: "75vh" } }}
    >
      <CategoryForm form={form} onSubmit={onSubmit} />
    </Modal>
  );
};

export default EditCategoryModal;
